import { ReactNode } from 'react'
import axios from 'axios'
import { Info, TriangleAlert } from 'lucide-react'
import { ClientRateconRevision } from '@numeo/types-main-web-app'
import { Badge, Input } from '@/components/ds2'

/** The revision's presentational pieces, shared by the board's review modal and the general-agent chat's revision card,
 *  so a revision reads the same wherever it is reviewed. */

/** One quiet inline note: a neutral surface, and only its icon carries a colour (danger for what can go wrong, muted otherwise), so a review reads as
 *  one calm page instead of a stack of coloured banners. */
export const RevisionNote = ({ tone = 'neutral', children, testId }: { tone?: 'neutral' | 'danger'; children: ReactNode; testId?: string }) => {
    const Icon = tone === 'danger' ? TriangleAlert : Info
    return (
        <div className="flex items-start gap-2 rounded-lg bg-surface-secondary px-2.5 py-1.5 text-xs leading-4 text-foreground-muted" data-testid={testId}>
            <Icon className={`mt-px h-3.5 w-3.5 shrink-0 ${tone === 'danger' ? 'text-danger' : 'text-foreground-muted'}`} aria-hidden="true" />
            <span className="min-w-0">{children}</span>
        </div>
    )
}

/** Someone else resolved this exact revision first — a second tab, a stale modal left open after
 *  the banner's list refreshed, or (rarely) a genuine double-click race. The backend's PENDING
 *  filter already prevented the double-apply; the only thing left to do here is tell the user why
 *  it failed (not "try again" — retrying fails identically) and let them close back out to a
 *  fresh, accurate list instead of staring at a dead modal. */
export const isAlreadyResolvedError = (err: unknown): boolean => axios.isAxiosError(err) && err.response?.status === 404

export interface DiffRow {
    /** The raw `changedFields` entry (e.g. `'rate'`, `'route[1].date'`) — unique per row, unlike `label`
     *  which collapses to the same text for same-field changes across multiple stops (e.g. two delivery
     *  stops both changing `date` would both render as "Delivery date"). Used as the React list key. */
    key: string
    label: string
    original: string
    revised: string
    delta: string
    deltaTone: 'positive' | 'negative' | 'neutral'
    /** Whether edit-merge may offer an input for this row. An address is not free text the backend
     *  can take verbatim — it has to geocode — so a moved stop is accepted as the con states it or
     *  not at all. */
    editable: boolean
}

const deltaToneClass: Record<DiffRow['deltaTone'], string> = {
    positive: 'text-success font-semibold',
    negative: 'text-danger font-semibold',
    neutral: 'text-foreground-muted',
}

/** FINANCIAL or SCHEDULE, and the rate's direction when it moved. */
export const RateconRevisionKindBadges = ({ revision }: { revision: Pick<ClientRateconRevision, 'v1' | 'v2' | 'changedFields'> }) => {
    const isFinancial = revision.changedFields.includes('rate')
    const rateDelta = revision.v2.rate - revision.v1.rate
    const direction = isFinancial && rateDelta !== 0 ? (rateDelta > 0 ? 'increase' : 'decrease') : null
    return (
        <>
            <Badge variant="soft" type="default" size="md" label={isFinancial ? 'FINANCIAL' : 'SCHEDULE'} />
            {direction && <Badge variant="soft" type="default" size="md" label={direction === 'increase' ? '▲ INCREASE' : '▼ DECREASE'} />}
        </>
    )
}

interface RateconRevisionDiffTableProps {
    revision: Pick<ClientRateconRevision, 'routeShapeChanged'>
    rows: DiffRow[]
    /** Edit-merge: the editable rows take an input holding `editValues[row.key]`. */
    isEditing?: boolean
    editValues?: Record<string, string>
    onEditValue?: (key: string, value: string) => void
    /** A narrow host (the Agent page's review drawer, 650px): no minimum width, every value wraps, so no column is cut off. */
    compact?: boolean
}

/** The field-level diff: each changed field's original, revised value and delta. */
export const RateconRevisionDiffTable = ({
    revision,
    rows,
    isEditing = false,
    editValues = {},
    onEditValue,
    compact = false,
}: RateconRevisionDiffTableProps) => (
    /* Bounded so stepping between a 2-field and a 9-field revision doesn't resize the
       dialog (it is centre-anchored, so a height change visibly shifts it). Long diffs
       scroll inside this box instead of growing it. */
    <div className={`max-h-[320px] min-h-[132px] rounded-xl ${compact ? 'overflow-y-auto overflow-x-hidden' : 'overflow-auto'}`}>
        <table className={`w-full table-fixed text-sm ${compact ? 'min-w-0 [&_td]:break-words' : 'min-w-[720px]'}`}>
            {/* Compact (the agent drawer) gives Δ room for a whole amount: "-$2,750" cannot wrap. */}
            <colgroup>
                <col className={compact ? 'w-[18%]' : 'w-[20%]'} />
                <col className={compact ? 'w-[25%]' : 'w-[29%]'} />
                <col className="w-[6%]" />
                <col className={compact ? 'w-[29%]' : 'w-[32%]'} />
                <col className={compact ? 'w-[22%]' : 'w-[13%]'} />
            </colgroup>
            <thead className="sticky top-0 z-[1] bg-surface">
                <tr className="text-left text-[11px] font-semibold uppercase tracking-wider text-foreground-muted">
                    <th className="py-2 pr-3">Field</th>
                    <th className="px-3 py-2">Original v1</th>
                    <th className="px-2 py-2" />
                    <th className="px-3 py-2">{isEditing ? 'Merged value' : 'Revised v2'}</th>
                    <th className="px-3 py-2">Δ</th>
                </tr>
            </thead>
            <tbody className="divide-y divide-border">
                {rows.length === 0 ? (
                    <tr>
                        <td className="px-3 py-3 text-foreground-muted" colSpan={5}>
                            {revision.routeShapeChanged
                                ? 'No field can be applied automatically — see the route below.'
                                : 'No tracked field changed. Accepting makes this rate confirmation the load’s document.'}
                        </td>
                    </tr>
                ) : (
                    rows.map((row) => (
                        <tr key={row.key}>
                            <td className="py-2 pr-3 align-top font-medium leading-5 text-foreground">{row.label}</td>
                            <td className="px-3 py-2 align-top leading-5 text-foreground-muted">
                                <span className="whitespace-normal break-words line-through decoration-foreground-muted">{row.original}</span>
                            </td>
                            <td className="px-2 py-2 text-center align-top text-foreground-muted">→</td>
                            <td className="px-3 py-2 align-top font-semibold leading-5 text-foreground">
                                {isEditing && row.editable ? (
                                    <Input
                                        inputType={row.key === 'rate' ? 'number' : 'text'}
                                        value={editValues[row.key] ?? ''}
                                        onChange={(e) => onEditValue?.(row.key, e.target.value)}
                                        aria-label={`Merged ${row.label}`}
                                        style={{ width: row.key === 'rate' ? 140 : 260 }}
                                    />
                                ) : (
                                    <span className="whitespace-normal break-words">{row.revised}</span>
                                )}
                            </td>
                            <td className={`${compact ? 'px-1.5' : 'px-3'} py-3 align-top`}>
                                <span
                                    className={`inline-flex max-w-full rounded-full bg-surface px-2.5 py-1 text-sm font-medium leading-5 ${compact ? 'whitespace-nowrap' : ''} ${deltaToneClass[row.deltaTone]}`}
                                >
                                    {isEditing ? '—' : row.delta}
                                </span>
                            </td>
                        </tr>
                    ))
                )}
            </tbody>
        </table>
    </div>
)

/** Whose paperwork this actually is. Only ever set on a hand-filed document, and only when it names a
 *  different load: reviewing it then means deciding whether to move THIS load's rate and stops onto
 *  what ANOTHER load's confirmation says. Renders nothing otherwise. */
export const RateconRevisionDocumentNotice = ({ revision }: { revision: Pick<ClientRateconRevision, 'extractedDocumentId' | 'rateConfDocumentId'> }) =>
    revision.extractedDocumentId ? (
        <div className="mt-3">
            <RevisionNote tone="danger">
                This rate confirmation names load <strong>{revision.extractedDocumentId}</strong>, not load {revision.rateConfDocumentId}. It was filed here by
                hand: check it belongs on this load before accepting, because accepting applies its rate and stops to this one.
            </RevisionNote>
        </div>
    ) : null
